import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'
import * as errorMessage from '../../error-message'

/**
 * Modal used for assigning users to Admin Center entities.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.text.Component,
    ds.input.checkbox.Component,
    ds.input.select.multiple.Component,
    ds.overlay.centered.Component,
    ds.layout.widget.Component,
    errorMessage.Component,
  ],
  selector: 'atn-admin-user-assignment-modal',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * Indicates whether in modal should be displayed multiselect for choosing
   * including/excluding app pages
   */
  public readonly hasAppPagesSg = ngCore.input<boolean>(false, {
    alias: 'hasAppPages',
  })

  /** @see {@link state.admin.userAssignment.Feature} */
  public readonly userAssignmentFeatureEc = ngCore.inject(
    state.admin.userAssignment.Feature,
  )

  /** @see {@link state.admin.appPages.Collection} */
  public readonly appPagesEc = ngCore.inject(state.admin.appPages.Collection)

  /** @see {@link state.admin.users.Collection} */
  private readonly _usersEc = ngCore.inject(state.admin.users.Collection)

  /** @see {@link state.admin.contexts.Feature} */
  private readonly _contextsFeature = ngCore.inject(
    state.admin.contexts.Feature,
  )

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /**
   * List of users available for assignment
   */
  public usersSg = ngCore.computed(
    (users = this.userAssignmentFeatureEc.selectors.users.asSignal()) =>
      users.map(user => ({ label: user.name, value: user.id })),
  )

  /**
   * List of available application pages that can be excluded from a user's
   * access permissions
   */
  public appPagesToExcludeSg = ngCore.computed(
    (
      appPages = this.appPagesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => appPages.map(page => ({ label: page.name, value: page.id })),
  )

  /**
   * List of available application pages that can be included in a user's
   * access permissions
   */
  public appPagesToIncludeSg = ngCore.computed(
    (
      appPages = this.appPagesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => appPages.map(page => ({ label: page.name, value: page.id })),
  )

  /**
   * List of currently selected page IDs that will be excluded from user's access
   * permissions
   */
  public excludePagesSg = ngCore.computed(
    (
      excludePages = this.userAssignmentFeatureEc.selectors.excludePages.asSignal(),
    ) => (excludePages ? Array.from(excludePages) : []),
  )

  /**
   * List of currently selected page IDs that will be included in user's access
   * permissions
   */
  public includePagesSg = ngCore.computed(
    (
      includePages = this.userAssignmentFeatureEc.selectors.includePages.asSignal(),
    ) => (includePages ? Array.from(includePages) : []),
  )

  /**
   * Whether a new tenant-level user can be created from this assignment modal.
   */
  public readonly canCreateTenantUserSg = ngCore.computed(
    (
      context = this._contextsFeature.selectors.activeEntityContext.asSignal(),
      currentUser = this._meFeature.selectors.currentUser.asSignal(),
    ) =>
      state.admin.userAssignment.models.User.isTenantContext(context) &&
      state.admin.userAssignment.models.User.canManageUsersForContext(
        currentUser,
        context,
      ),
  )

  /** Discard all changes that have been edited */
  private _discardChanges(source: string): void {
    this.userAssignmentFeatureEc.actions.reset.from(source).dispatch()
  }

  /**
   * Handle users selection change
   */
  public selectedUsersChangeHandler(items: readonly unknown[]): void {
    this.userAssignmentFeatureEc.actions.setSelectedUserIds
      .from('UserAssignment.Modal.MultiSelect.Value.Changed')
      .dispatch({
        ids: items.filter(ents.core.admin.User.Id.isId).map(item => item),
      })
  }

  /**
   * Opens the user modal for creating a tenant-level user.
   */
  public createNewUserClickHandler(): void {
    this._discardChanges('UserAssignment.Modal.CreateNewUser.Button.Clicked')
    this._usersEc.plugins.editingPlugin.feature.actions.edit
      .from('UserAssignment.Modal.CreateNewUser.Button.Clicked')
      .dispatch({
        edit: {
          input: {
            isTenant: true,
          },
        },
        makeCurrent: {
          discardPrevious: true,
        },
      })
  }

  /**
   * Handle app pages exclusion selection change
   */
  public excludedAppPagesChangeHandler(items: readonly unknown[]): void {
    this.userAssignmentFeatureEc.actions.setExcludedPages
      .from('UserAssignment.Modal.MultiSelect.Value.Changed')
      .dispatch({
        ids: new Set(items.filter(ents.core.admin.App.Page.Id.isId)),
      })
  }

  /**
   * Handle app pages inclusion selection change
   */
  public includedAppPagesChangeHandler(items: readonly unknown[]): void {
    this.userAssignmentFeatureEc.actions.setIncludedPages
      .from('UserAssignment.Modal.MultiSelect.Value.Changed')
      .dispatch({
        ids: new Set(items.filter(ents.core.admin.App.Page.Id.isId)),
      })
  }

  /**
   * Returns true Flag is selected
   */
  public isFlagSelected(flag: ents.core.admin.Access.Flag): boolean {
    return ents.core.admin.Access.allows(
      this.userAssignmentFeatureEc.selectors.access.asSignal(),
      flag,
    )
  }

  /** Submit changes handler */
  public saveClickHandler(): void {
    this.userAssignmentFeatureEc.actions.assignAccess
      .from('UserAssignment.Modal.Save.Button.Clicked')
      .dispatch({
        assignments:
          this.userAssignmentFeatureEc.selectors.assignments.asSignal(),
      })
  }

  /**
   * Cancel button handler
   */
  public cancelButtonClickHandler(): void {
    this._discardChanges('UserAssignment.Modal.Cancel.Button.Clicked')
  }

  /**
   * Close icon handler
   */
  public closeIconClickHandler(): void {
    this._discardChanges('UserAssignment.Modal.Close.Icon.Clicked')
  }

  /** Checkbox value change handler */
  public accessValueChangeHandler(isSelected: boolean, flag: number): void {
    const currentAccess =
      this.userAssignmentFeatureEc.selectors.access.asSignal()
    const access = isSelected
      ? ents.core.admin.Access.add(currentAccess, flag)
      : ents.core.admin.Access.remove(currentAccess, flag)
    this.userAssignmentFeatureEc.actions.setAccess
      .from('UserAssignment.Modal.Checkbox.Icon.Clicked')
      .dispatch({ access })
  }
}
